import { HeroStyle2 } from "~/components/HeroStyle2";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./meet-the-team.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { HoverRevealImages } from "~/components/HoverRevealImages";

type Localization = {
  title: string;
  meetOurFamilyHeading: string;
  meetOurFamilyContent1: string;
  meetOurFamilyContent2: string;
  teamTitle: string;
  teamIntroP1: string;
  teamIntroP2: string;
  teamMissionP1: string;
  teamMissionP2: string;
};

const englishLocalization: Localization = {
  title: "Meet the Team",
  meetOurFamilyHeading: "Meet Our Family",
  meetOurFamilyContent1:
    "The Miami Orthodontic Specialists team is knowledgeable, friendly, and eager to guide you through the teeth straightening process.",
  meetOurFamilyContent2:
    "With expertise in braces and Invisalign, we can meet and exceed your every need.",
  teamTitle: "Our Team Of Smile Specialists",
  teamIntroP1:
    "When Dr. Garcia-Paul first started Miami Orthodontic Specialists, she knew it was critical to find people that believed in the same vision that she did – never compromising on quality or service.",
  teamIntroP2:
    "One-by-one, she assembled her team of Smile Specialists who not only focus on personalized care but genuinely treat our patients like family.",
  teamMissionP1: "Our patients are the focus of our practice.",
  teamMissionP2:
    "We want to transform the way you smile from the beginning to the end of your experience with us.",
};

const spanishLocalization: Localization = {
  title: "Conozca al equipo",
  meetOurFamilyHeading: "Conozca a nuestra familia",
  meetOurFamilyContent1:
    "El equipo de Miami Orthodontic Specialists es experto, amigable y está ansioso por guiarlo a través del proceso de enderezamiento de dientes.",
  meetOurFamilyContent2:
    "Con experiencia en brackets e Invisalign, podemos satisfacer y superar todas sus necesidades.",
  teamTitle: "Nuestro Equipo de Especialistas en Sonrisas",
  teamIntroP1:
    "Cuando la Dra. García-Paul comenzó Miami Orthodontic Specialists, sabía que era fundamental encontrar personas que creyeran en la misma visión que ella – nunca comprometer la calidad ni el servicio.",
  teamIntroP2:
    "Uno a uno, formó su equipo de Especialistas en Sonrisas que no solo se enfocan en la atención personalizada sino que genuinamente tratan a nuestros pacientes como familia.",
  teamMissionP1: "Nuestros pacientes son el enfoque de nuestra práctica.",
  teamMissionP2:
    "Queremos transformar tu sonrisa desde el principio hasta el final de tu experiencia con nosotros.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

function EmployeeCard({
  name,
  title,
  topImage,
  bottomImage,
}: {
  name: string;
  title: string;
  topImage: string;
  bottomImage: string;
}) {
  return (
    <article className={styles.employeeCard}>
      <HoverRevealImages
        topImage={topImage}
        bottomImage={bottomImage}
        alt={`Miami Orthodontic Specialists team member ${name}`}
        className={styles.employeeImage}
        fill
      />
      <Flex
        direction="column"
        justify="center"
        align="center"
        width="100%"
        pt="4"
        px="5"
        gap="4"
      >
        <Heading as="h4" align="center" className={styles.employeeName}>
          {name}
        </Heading>
        {title ? (
          <Text align="center" className={styles.employeeTitle}>
            {title}
          </Text>
        ) : null}
      </Flex>
    </article>
  );
}

const teamMembers = [
  ["Oisis", "/team-oisis.jpg", "/team-oisis-alt.jpg"],
  ["Luisa", "/team-luisa.jpg", "/team-luisa-alt.jpg"],
  ["Ronald", "/team-ronald.jpg", "/team-ronald-alt.jpg"],
  ["Velia", "/team-velia.jpg", "/team-velia-alt.jpg"],
  ["Bibi", "/team-bibi.jpg", "/team-bibi-alt.jpg"],
  ["Lizbeth", "/team-lizbeth.jpg", "/team-lizbeth-alt.jpg"],
  ["Claudia", "/team-claudia.jpg", "/team-claudia-alt.jpg"],
  ["Maribel", "/team-maribel.jpg", "/team-maribel-alt.jpg"],
  ["Scarlette", "/team-scarlette.jpg", "/team-scarlette-alt.jpg"],
  ["Ivette", "/team-ivette.jpg", "/team-ivette-alt.jpg"],
  ["Alyssa", "/team-alyssa.jpg", "/team-alyssa-alt.jpg"],
  ["Kelly", "/team-kelly.jpg", "/team-kelly-alt.jpg"],
] as const;

export function MeetTheTeamPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <HeroStyle2
        heroImageUrl="/meet-the-team-header-2026.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
      />
      <Flex
        className={`section ${styles.meetOurFamilySection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="7" className={`innerSection`} p="5">
          <Heading as="h2" className={styles.heading} weight="bold">
            {getLocalization("meetOurFamilyHeading")}
          </Heading>
          <Text
            as="p"
            size="5"
            style={{
              lineHeight: "1.55em",
              letterSpacing: "normal",
              textWrap: "pretty",
            }}
          >
            {getLocalization("meetOurFamilyContent1")}
          </Text>
          <Text
            as="p"
            size="5"
            style={{
              lineHeight: "1.55em",
              letterSpacing: "normal",
              textWrap: "pretty",
            }}
          >
            {getLocalization("meetOurFamilyContent2")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        className={`section ${styles.teamIntrosSectionOuter}`}
        width="100%"
        justify="center"
        align="center"
      >
        <Flex
          gap="4"
          direction="column"
          width="100%"
          className={styles.teamIntrosSection}
        >
          <Heading as="h3" className={styles.subsectionHeading}>
            {getLocalization("teamTitle")}
          </Heading>
          <Flex
            gap="6"
            direction="row"
            width="100%"
            wrap="wrap"
            justify="center"
          >
            <Flex
              gap="6"
              flexBasis={{ initial: "100%", sm: "48%" }}
              direction="column"
            >
              <Text as="p" size="5">
                {getLocalization("teamIntroP1")}
              </Text>
              <Text as="p" size="5">
                {getLocalization("teamIntroP2")}
              </Text>
            </Flex>
            <Flex
              gap="6"
              flexBasis={{ initial: "100%", sm: "48%" }}
              direction="column"
            >
              <Text as="p" size="5">
                {getLocalization("teamMissionP1")}
              </Text>
              <Text as="p" size="5">
                {getLocalization("teamMissionP2")}
              </Text>
            </Flex>
          </Flex>
          <div className={styles.teamMembersGrid}>
            {teamMembers.map(([name, topImage, bottomImage]) => (
              <EmployeeCard
                key={name}
                topImage={topImage}
                bottomImage={bottomImage}
                name={name}
                title=""
              />
            ))}
          </div>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
